/* eslint-disable @typescript-eslint/no-explicit-any */
import { IMapperTable, IMapperConnectionInfo, SchemaLoadStatus, ICassandraMapperEntity } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DatasetTypeHandler } from './datasettype-handler';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';
import { CassandraSettings } from 'app/model/dataflow/dataset-settings/cassandra-settings';
import { DataFlowExpression, createDataflowExpression } from 'app/model/dataflow/dataflowexpression';
import { Dataset, formatConst, LinkedService, Metamodel, Named, Owned, storeConst } from 'shell-models-lib';
import { CassandraDataset } from 'app/model/dataset/cassandra.dataset';
import { getValidTransformationName } from 'app/model/adfDataMapper/adfDataMapperUtils';
import { QueryApiFactoryService } from 'app/apis/query-api.factory.service';
import { Subject, first } from 'rxjs';
import { AdfDataMapper } from 'app/model/adfDataMapper/adfDataMapper';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { LoggerService } from 'app/logger/logger.service';

export class CassandraDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
    ){
        super();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getOkButtonStatus(selectedRows: any[]): [boolean, string] {
        if (selectedRows.length > 0) {
            return [false, ''];
        }

        return [true, ''];
    }

    public processSelectedRows(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selectedRows: any[],
        selectedSourceLinkedService: LinkedService,
        selectedDatesetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dslProps = new Map<string, any>([
                [DslConnectorConstants.keyspace, DataFlowExpressionUtils.escapeSingleQuoteInDslString(elem.schemaName)],
                [DslConnectorConstants.tableName, DataFlowExpressionUtils.escapeSingleQuoteInDslString(elem.tableName)]
            ]);
            if (!!elem.watermarkSelected) {
                dslProps.set('enableCdc', true);
                dslProps.set(DslConnectorConstants.waterMarkColumn, elem.watermarkSelected);
            }
            tables.push({
                name: getValidTransformationName(true, elem.entityName),
                sourceLinkedService: selectedSourceLinkedService,
                isInlineDataset: true,
                sourceDataset: elem.dataset,
                datasetSettings: undefined,
                wildcardPaths: [],
                dslConnectorProperties: dslProps,
                datasetType: DatasetFormatType.Cassandra,
                schemaLoadStatus: SchemaLoadStatus.NOT_STARTED,
                tableName: elem.tableName
            });
        });

        return tables;
    }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('CassandraSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as CassandraSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as CassandraSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.tableName.expression = incomingSourceDatasetsSettings.tableName.expression ? undefined : incomingSourceDatasetsSettings.tableName.expression;
        outputSourceDatasetsSettings.schemaName.expression = incomingSourceDatasetsSettings.schemaName.expression ? undefined : incomingSourceDatasetsSettings.schemaName.expression;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        const schemaName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.keyspace);
        const tableName = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);

        const cassandraSettings = Owned.create<CassandraSettings>(CassandraSettings.MODEL, table as DataFlowSource);
        cassandraSettings.initialize(undefined);
        cassandraSettings.schemaName = createDataflowExpression(schemaName) as DataFlowExpression;
        cassandraSettings.tableName = createDataflowExpression(tableName) as DataFlowExpression;

        return cassandraSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const schemaName: string = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.keyspace);
        const tableName: string = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.tableName);
        const dataset = new CassandraDataset('CassandraTable');
        dataset.linkedService = linkedService;
        dataset.table = tableName;
        dataset.tableName = tableName;
        const resourceInRaw = {
            properties: {
                typeProperties: {
                    tableName,
                    keyspace: schemaName
                }
            }
        };

        dataset.raw = JSON.stringify(resourceInRaw);

        return dataset;
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions?: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);

        const commonDslConnectorProperties = mapperConnectionInfo.connection.commonDslConnectorProperties;
        commonDslConnectorProperties.set(DslConnectorConstants.format, formatConst.Cassandra);
        commonDslConnectorProperties.set(DslConnectorConstants.store, storeConst.Cassandra);
        commonDslConnectorProperties.set('enableCDC', true);

        if (this.isSource) {
            commonDslConnectorProperties.set('skipInitialLoad', true);
        }
    }

    public async fetchEntities(loggerService: LoggerService, queryApiFactoryService: QueryApiFactoryService, linkedService: LinkedService, isSource?: boolean,
        _selectedDatasetFormat?: DatasetFormatType, isDebugSessionActive: () => Promise<boolean> = undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getEntitiesForADataFlowSource: (dataflowSource: DataFlowSource, resultSubject: Subject<ICassandraMapperEntity[]>) => Promise<void> = undefined): Promise<any[]> {
        const dataMappingModel: AdfDataMapper = new AdfDataMapper('datamapper');
        const dataflow: DataFlow = Named.create<DataFlow>(DataFlow.MODEL, dataMappingModel);
        const source: DataFlowSource = Named.create<DataFlowSource>(DataFlowSource.MODEL, dataflow);
        source.sourceLinkedService = linkedService;
        const resultSubject = new Subject<ICassandraMapperEntity[]>();
        const cassandraSettings = Owned.create<CassandraSettings>(CassandraSettings.MODEL, source);
        cassandraSettings.initialize(undefined);
        source.datasetSettings = cassandraSettings;
        source.isInlineDataset = true;
        source.name = 'temp';
        dataflow.addSource(source);
        dataflow.name = 'temp';
        source.addOutputStream();

        await getEntitiesForADataFlowSource(source, resultSubject);
        const results = await resultSubject.pipe(first()).toPromise();
        const result: ICassandraMapperEntity[] = [];
        results.forEach((elem) => result.push(elem));

        return result;
    }
}
